import React, { Fragment } from 'react';
import { Field, FieldArray, Formik, setNestedObjectValues } from 'formik';
import * as Yup from 'yup';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { CountryDropdown } from 'components/layout/form-fields/inputs/CountryDropdown';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';
import {
	Button,
	FormColumn,
	FormField,
	FormRow,
	FormSection,
	TagList
} from 'components/layout/modals/components';
import { ProfileImageInput } from 'components/layout/form-fields/inputs/ProfileImageInput';
import { applicantStore } from '../ApplicantStore';
import UserAssignmentInput from 'ats/components/user-assignment-input/UserAssignmentInput';
import ApplicantAssignmentInput from 'ats/components/applicant-assignment-input/ApplicantAssignmentInput';
import { authStore } from 'shared/security/AuthStore';
import CheckboxGroup from 'ats/components/checkbox-group/CheckboxGroup';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { CurrencyDropdown } from 'components/layout/form-fields/inputs/CurrencyDropdown';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { candidateSeniorityOptions } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { getHireabilityDropdownOptions } from 'ats/utils/hireabilityDropdownOptions';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import TagsDropdown from 'ats/components/tags-dropdown/TagsDropdown';
import { countryStore } from 'components/settings/countries/CountryStore';
import {
	notifyDuplicateApplicant,
	withApplicantEmailUniqueValidation,
	withApplicantLinkedinUniqueValidation
} from './applicantFormValidation';
import { openApplicantProfile } from 'ats/utils/openEntityProfile';
import { formatNumberWithCommas } from 'shared/utils';
import { CompensationAmountInput } from './CompensationAmountInput';

const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];
const MAX_FILE_SIZE_LIMIT = 1000 * 1024;

const buildValidationSchema = onDuplicate =>
	Yup.object().shape({
		name: Yup.string().required().label('Name'),
		email: withApplicantEmailUniqueValidation(
			Yup.string()
				.transform((value, originalValue) => {
					if (typeof originalValue !== 'string') return originalValue;
					const trimmed = originalValue.trim();
					return trimmed === '' ? null : trimmed;
				})
				.nullable()
				.email()
				.label('Email'),
			undefined,
			onDuplicate
		),
		city: Yup.string().label('City'),
		phoneNumber: Yup.string().label('Phone'),
		seniority: Yup.string().nullable().label('Seniority'),
		hireability: Yup.string().nullable().label('Hireability'),
		englishLevel: Yup.string().nullable().label('English Level'),
		role: Yup.object().nullable().label('Role'),
		potentialJobRoles: Yup.array()
			.of(Yup.object())
			.label('Potential Roles')
			.test(
				'role-must-be-saved',
				"You have selected a role but haven't added it to the potential roles list. Please click the save button to add it.",
				function (value) {
					const { role } = this.parent;

					if (role && role.id) {
						// Check if the selected role exists in potentialJobRoles
						const roleExists =
							value &&
							value.some(potentialRole => potentialRole.id === role.id);
						return roleExists;
					}
					return true;
				}
			)
			.min(1)
			.required(),
		compensations: Yup.array()
			.of(
				Yup.object().shape({
					type: Yup.object().nullable().required().label('Type'),
					currency: Yup.object().nullable().required().label('Currency'),
					scheme: Yup.object().nullable().required().label('Scheme'),
					amount: Yup.number()
						.nullable()
						.required()
						.min(1, 'Amount must be greater than 0')
						.label('Amount')
				})
			)
			.label('Compensations'),
		editingCompensationIndex: Yup.number()
			.nullable()
			.test(
				'must-be-null',
				'Please click the Save button to save your compensation entry before submitting the form.',
				value => value === null
			)
			.label('New Compensation'),
		tags: Yup.array().of(Yup.object()).label('Tags'),
		additionalTags: Yup.array().of(Yup.object()).label('Additional Tags'),
		linkedinUrl: withApplicantLinkedinUniqueValidation(
			Yup.string().nullable().url().label('LinkedIn URL'),
			undefined,
			onDuplicate
		),
		githubUrl: Yup.string().url().label('GitHub URL'),
		portfolioUrl: Yup.string().url().label('Portfolio URL'),
		countryId: Yup.string().nullable().label('Country'),
		ownerId: Yup.string().nullable().label('Owner'),
		sourceId: Yup.string().nullable().label('Source'),
		referrerId: Yup.string().nullable().label('Referrer'),
		profileImage: Yup.mixed()
			.nullable()
			.test(
				'only-image-files',
				'File type not supported. Use .png, .jpg, .jpeg or .gif.',
				value => {
					return value && typeof value !== 'string'
						? SUPPORTED_FORMATS.includes(value.type)
						: true;
				}
			)
			.test(
				'file-size-limit',
				'The image file size is too large (max 1MB)',
				value =>
					value && typeof value !== 'string'
						? value.size <= MAX_FILE_SIZE_LIMIT
						: true
			)
			.label('Profile Image')
	});

export class ApplicantCreate extends ModalComponent {
	static isV2 = true;

	formikRef = React.createRef();
	importValidationRan = false;

	state = {
		isEditingPotentialJobRoles: false,
		isReferral: false,
		featuredTags: [],
		importedData: null,
		foundCountry: null,
		isLoadingInitialData: true
	};

	async componentDidMount() {
		const featuredTags = await applicantStore.loadFeaturedTags();
		this.setState({ featuredTags });

		await this.loadImportedData();

		this.setState({ isLoadingInitialData: false });
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.isLoadingInitialData && !this.state.isLoadingInitialData) {
			this.validateImportedForm();
		}
	}

	validateImportedForm() {
		if (this.importValidationRan) return;

		const importedData = this.props.importedData || this.state.importedData;
		if (!importedData || !this.formikRef.current) return;

		this.importValidationRan = true;
		const { validateForm, setTouched } = this.formikRef.current;

		validateForm().then(errors => {
			if (errors && Object.keys(errors).length > 0) {
				setTouched(setNestedObjectValues(errors, true));
			}
		});
	}

	async loadImportedData() {
		if (this.props.importedData) {
			this.setState({ importedData: this.props.importedData });

			if (this.props.importedData.countryName) {
				try {
					const result = await countryStore.getCountries(
						this.props.importedData.countryName
					);
					if (result.data && result.data.length > 0) {
						const matchedCountry = result.data.find(
							country =>
								country.name.toLowerCase() ===
								this.props.importedData.countryName.toLowerCase()
						);
						if (matchedCountry) {
							this.setState({ foundCountry: matchedCountry });
						}
					}
				} catch (error) {
					console.error('Error fetching country:', error);
				}
			}
		}
	}

	getInitialValues() {
		const importedData = this.props.importedData || this.state.importedData;
		const foundCountry = this.state.foundCountry;

		const defaultValues = {
			name: '',
			email: '',
			phoneNumber: '',
			seniority: null,
			city: '',
			profileImage: null,
			linkedinUrl: null,
			githubUrl: '',
			portfolioUrl: '',
			hireability: null,
			englishLevel: null,
			tags: [],
			additionalTags: [],
			role: null,
			potentialJobRoles: [],
			compensations: [],
			editingCompensationIndex: null,
			owner: null,
			ownerId: null,
			source: null,
			sourceId: null,
			referrer: null,
			referrerId: null,
			countryId: null,
			country: null
		};

		if (importedData) {
			return {
				...defaultValues,
				name: importedData.name || '',
				email: importedData.email || '',
				phoneNumber: importedData.phoneNumber || '',
				city: importedData.city || '',
				linkedinUrl: importedData.linkedinUrl || null,
				profileImage: importedData.profileImageUrl || null,
				country: foundCountry,
				countryId: foundCountry?.id
			};
		}

		return defaultValues;
	}

	validationSchema = buildValidationSchema(notifyDuplicateApplicant);

	render() {
		if (this.state.isLoadingInitialData) {
			return null;
		}

		return (
			<Formik
				innerRef={this.formikRef}
				initialValues={this.getInitialValues()}
				validationSchema={this.validationSchema}
				validateOnBlur={false}
				validateOnChange={false}
				onSubmit={values => {
					this.saving();
					this.create(values);
				}}>
				{formikBag => this.renderModalContainer(formikBag, 'ApplicantCreate')}
			</Formik>
		);
	}

	renderForm(formikBag) {
		return (
			<Fragment>
				<FormField
					variant="blue"
					label={<img src="/ats/icons/linkedin-blue-icon.svg" alt="LinkedIn" />}
					error={formikBag.errors.linkedinUrl}>
					<FormikFormField
						className="has-text-left"
						placeholder="https://linkedin.com/in/"
						field="linkedinUrl"
						type="text"
						showErrors={false}
						onBlur={() => formikBag.validateField('linkedinUrl')}
					/>
				</FormField>
				<FormRow>
					<FormSection size={1 / 2}>
						<Field
							component={ProfileImageInput}
							name="profileImage"
							size={120}
							placeholder={
								formikBag.values.name?.trim()
									? formikBag.values.name.trim()[0].toUpperCase()
									: 'C'
							}
							className="mx-auto"
						/>
						<FormField
							label="Name"
							required
							error={formikBag.errors.name}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="name"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Country"
							variant="dashed"
							error={formikBag.errors.countryId}>
							<CountryDropdown
								value={formikBag.values.country}
								placeholder=" "
								styleVersion="ats"
								onValueChange={value => {
									formikBag.setFieldValue('country', value);
									formikBag.setFieldValue('countryId', value?.id);
								}}
							/>
						</FormField>
						<FormField
							label="City"
							error={formikBag.errors.city}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="city"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Email"
							error={formikBag.errors.email}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="email"
								type="email"
								placeholder=" "
								showErrors={false}
								onBlur={() => formikBag.validateField('email')}
							/>
						</FormField>
						<FormField
							label="Phone"
							error={formikBag.errors.phoneNumber}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="phoneNumber"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label={<img src="/ats/icons/git-icon.svg" alt="GitHub" />}
							error={formikBag.errors.githubUrl}
							variant="dashed blue">
							<FormikFormField
								className="has-text-left"
								field="githubUrl"
								type="text"
								placeholder="https://github.com/"
								showErrors={false}
							/>
						</FormField>
						<FormField
							label={
								<img src="/ats/icons/globe-blue-icon.svg" alt="Portfolio" />
							}
							error={formikBag.errors.portfolioUrl}
							variant="dashed blue">
							<FormikFormField
								className="has-text-left"
								field="portfolioUrl"
								type="text"
								placeholder="https://portfolio.com"
								showErrors={false}
							/>
						</FormField>
					</FormSection>
					<FormColumn>
						<FormSection
							title="Potential Role (s)"
							required
							actionButton={
								<Button
									variant="no-style"
									onClick={e => {
										e.preventDefault();

										this.setState({
											isEditingPotentialJobRoles: true
										});
									}}>
									<img src="/ats/icons/plus-blue-icon.svg" alt="New Contact" />{' '}
									Add Potential Role
								</Button>
							}>
							<FormField error={formikBag.errors.potentialJobRoles}>
								<FieldArray name="potentialJobRoles">
									{({ replace, remove, insert }) => (
										<>
											{this.state.isEditingPotentialJobRoles ? (
												<div className="is-flex gap-2">
													<FormField error={formikBag.errors.role}>
														<JobRoleDropdown
															isMulti={false}
															value={formikBag.values.role}
															placeholder="New Role"
															styleVersion="ats"
															onValueChange={value => {
																formikBag.setFieldValue('role', value);

																this.setState({
																	isEditingPotentialJobRoles: false
																});

																insert(0, {
																	...value,
																	isPrimary:
																		formikBag.values.potentialJobRoles
																			.length === 0
																});

																formikBag.setFieldValue('role', null);
															}}
														/>
													</FormField>
												</div>
											) : null}
											{formikBag.values.potentialJobRoles?.map(
												(potentialRole, idx) => {
													return (
														<div key={idx} className="field-array-item">
															<button
																className="set-primary-btn"
																title="Primary"
																onClick={e => {
																	e.preventDefault();

																	const updatedPotentialJobRoles =
																		formikBag.values.potentialJobRoles.map(
																			(potentialRole, i) => ({
																				...potentialRole,
																				isPrimary:
																					i === idx
																						? !potentialRole.isPrimary
																						: false
																			})
																		);

																	formikBag.setFieldValue(
																		'potentialJobRoles',
																		updatedPotentialJobRoles
																	);
																}}
																type="button">
																<img
																	src={
																		potentialRole.isPrimary
																			? '/ats/icons/star-filled.svg'
																			: '/ats/icons/star-empty.svg'
																	}
																	alt="Star"
																/>
															</button>
															{potentialRole.name}
															<button
																title="Remove Potential Role"
																className="remove-btn"
																onClick={e => {
																	e.preventDefault();
																	remove(idx);

																	if (potentialRole.isPrimary) {
																		replace(0, {
																			...formikBag.values.potentialJobRoles[0],
																			isPrimary: true
																		});
																	}
																}}
																type="button">
																<img
																	src="/ats/icons/trash-icon.svg"
																	alt="Delete"
																/>
															</button>
														</div>
													);
												}
											)}
										</>
									)}
								</FieldArray>
							</FormField>
						</FormSection>
						<FormSection title="Team">
							<FormField
								label={this.state.isReferral ? 'Referrer' : 'Source'}
								error={
									this.state.isReferral
										? formikBag.errors.referrerId
										: formikBag.errors.sourceId
								}>
								{this.state.isReferral ? (
									<ApplicantAssignmentInput
										value={formikBag.values.referrer}
										placeholder=" "
										onChange={value => {
											formikBag.setFieldValue('referrer', value);
											formikBag.setFieldValue('referrerId', value?.id || null);
										}}
										customFieldActions={
											<div className="referral-checkbox">
												<input
													type="checkbox"
													id="source-checkbox"
													checked={this.state.isReferral}
													onChange={e => {
														this.setState({ isReferral: e.target.checked });
														if (!e.target.checked) {
															formikBag.setFieldValue('referrer', null);
															formikBag.setFieldValue('referrerId', null);
														}
													}}
												/>
												<label htmlFor="source-checkbox">Referral</label>
											</div>
										}
									/>
								) : (
									<UserAssignmentInput
										value={formikBag.values.source}
										placeholder=" "
										role={NON_CANDIDATE_USER_ROLES}
										onChange={value => {
											formikBag.setFieldValue('source', value);
											formikBag.setFieldValue('sourceId', value?.id || null);
										}}
										onAssignToMe={() => {
											const currentUser = authStore.profile;
											formikBag.setFieldValue('source', currentUser);
											formikBag.setFieldValue('sourceId', currentUser.id);
										}}
										customFieldActions={
											<div className="referral-checkbox">
												<input
													type="checkbox"
													id="source-checkbox"
													checked={this.state.isReferral}
													onChange={e => {
														this.setState({ isReferral: e.target.checked });
														if (e.target.checked) {
															formikBag.setFieldValue('source', null);
															formikBag.setFieldValue('sourceId', null);
														}
													}}
												/>
												<label htmlFor="source-checkbox">Referral</label>
											</div>
										}
									/>
								)}
							</FormField>
							<FormField label="Owner" error={formikBag.errors.ownerId}>
								<UserAssignmentInput
									value={formikBag.values.owner}
									placeholder=" "
									role={NON_CANDIDATE_USER_ROLES}
									onChange={value => {
										formikBag.setFieldValue('owner', value);
										formikBag.setFieldValue('ownerId', value?.id || null);
									}}
									onAssignToMe={() => {
										const currentUser = authStore.profile;
										formikBag.setFieldValue('owner', currentUser);
										formikBag.setFieldValue('ownerId', currentUser.id);
									}}
								/>
							</FormField>
						</FormSection>
						<FormSection title="Details">
							<FormRow className="is-align-items-center">
								<CheckboxGroup
									options={this.state.featuredTags.map(tag => ({
										label: tag.name,
										value: tag.name.toLowerCase().replace(/\s+/g, '_'),
										id: tag.id
									}))}
									value={formikBag.values.tags}
									onValueChange={value => {
										formikBag.setFieldValue('tags', value);
									}}
									colors={{
										checked: {
											background: '#6938EF',
											text: '#FFFFFF',
											checkbox: {
												background: '#6938EF',
												border: '#6938EF'
											}
										},
										unchecked: {
											background: '#FFFFFF',
											text: '#4A1FB8',
											border: '#F4F3FF'
										}
									}}
									allowMultiple
									className="tags-group-checkbox"
								/>
								<div className="form-section-action-button">
									<TagsDropdown
										target={({ onClick, ref, ...props }) => (
											<Button
												variant="no-style"
												onClick={onClick}
												ref={ref}
												{...props}>
												<img
													src="/ats/icons/plus-blue-icon.svg"
													alt="Add tags"
												/>{' '}
												Add Tag
											</Button>
										)}
										value={formikBag.values.additionalTags}
										onChange={values =>
											formikBag.setFieldValue('additionalTags', values)
										}
										optionsFilter={options =>
											options.filter(
												option =>
													!applicantStore.featuredTagsOptions.includes(
														option.name
													)
											)
										}
									/>
								</div>
							</FormRow>
							<FormRow>
								{formikBag.values.additionalTags?.length > 0 && (
									<TagList
										tags={formikBag.values.additionalTags}
										onRemove={value =>
											formikBag.setFieldValue(
												'additionalTags',
												formikBag.values.additionalTags.filter(
													tag => tag.id !== value.id
												)
											)
										}
										getTagVariant={value => 'outline'}
										textKey="name"
									/>
								)}
							</FormRow>
							<FormRow>
								<FormField
									label="English Level"
									error={formikBag.errors.englishLevel}
									variant="dashed bottom">
									<Dropdown
										value={formikBag.values.englishLevel}
										placeholder=" "
										menuClassName="english-level-dropdown"
										onChange={item => {
											formikBag.setFieldValue('englishLevel', item);
										}}
										options={candidatesStore.englishLevelOptions.map(
											option => ({
												...option,
												label: (
													<div
														className="tooltip is-tooltip-right"
														data-tooltip={option.tooltip}>
														{option.text}
													</div>
												)
											})
										)}
										optionValue="value"
									/>
								</FormField>
								<FormField
									label="Seniority"
									error={formikBag.errors.seniority}
									variant="dashed bottom">
									<Dropdown
										value={formikBag.values.seniority}
										placeholder=" "
										onChange={item => {
											formikBag.setFieldValue('seniority', item);
										}}
										options={candidateSeniorityOptions}
										optionValue="value"
									/>
								</FormField>
								<FormField
									label="Hireability"
									error={formikBag.errors.hireability}
									variant="dashed bottom">
									<Dropdown
										value={formikBag.values.hireability}
										placeholder=" "
										onChange={item => {
											formikBag.setFieldValue('hireability', item);
										}}
										options={getHireabilityDropdownOptions()}
										optionValue="value"
									/>
								</FormField>
							</FormRow>
						</FormSection>
						<FormSection
							title="Compensation"
							actionButton={
								<Button
									variant="no-style"
									onClick={e => {
										e.preventDefault();

										if (
											formikBag.values.compensations?.[0] &&
											(!formikBag.values.compensations[0].type ||
												!formikBag.values.compensations[0].scheme ||
												!formikBag.values.compensations[0].currency ||
												!formikBag.values.compensations[0].amount)
										) {
											return;
										}

										formikBag.setFieldValue('compensations', [
											{
												type: null,
												scheme: null,
												amount: null,
												currency: null
											},
											...(formikBag.values.compensations || [])
										]);
										formikBag.setFieldValue('editingCompensationIndex', 0);
									}}>
									<img
										src="/ats/icons/plus-blue-icon.svg"
										alt="Add Compensation"
									/>{' '}
									Add Compensation
								</Button>
							}>
							<table className="table-field compensations-table table is-fullwidth">
								<thead />
								<tbody>
									<FieldArray name="compensations">
										{({ remove }) =>
											formikBag.values.compensations?.map(
												(compensation, idx) => {
													const isEditing =
														formikBag.values.editingCompensationIndex === idx;
													return (
														<>
															<tr key={idx}>
																<td>
																	{isEditing ? (
																		<FormField
																			error={
																				formikBag.errors.compensations?.[idx]
																					?.type
																			}
																			showErrorMessage={false}
																			showErrorBorder>
																			<Dropdown
																				value={compensation.type}
																				placeholder=" "
																				onChange={item => {
																					formikBag.setFieldValue(
																						`compensations.${idx}.type`,
																						item
																					);
																				}}
																				options={
																					applicantStore.compensationsOptions
																				}
																			/>
																		</FormField>
																	) : (
																		compensation.type?.label
																	)}
																</td>
																<td>
																	{isEditing ? (
																		<FormField
																			error={
																				formikBag.errors.compensations?.[idx]
																					?.currency
																			}
																			showErrorMessage={false}
																			showErrorBorder>
																			<CurrencyDropdown
																				value={compensation.currency}
																				placeholder=" "
																				styleVersion="ats"
																				onValueChange={item => {
																					formikBag.setFieldValue(
																						`compensations.${idx}.currency`,
																						item
																					);
																				}}
																			/>
																		</FormField>
																	) : (
																		compensation.currency?.code || '-'
																	)}
																</td>
																<td>
																	{isEditing ? (
																		<FormField
																			error={
																				formikBag.errors.compensations?.[idx]
																					?.scheme
																			}
																			showErrorMessage={false}
																			showErrorBorder>
																			<Dropdown
																				value={compensation.scheme}
																				placeholder=" "
																				onChange={item => {
																					formikBag.setFieldValue(
																						`compensations.${idx}.scheme`,
																						item
																					);
																				}}
																				options={
																					applicantStore.compensationsTypesOptions
																				}
																			/>
																		</FormField>
																	) : (
																		compensation.scheme?.label
																	)}
																</td>
																<td>
																	{isEditing ? (
																		<FormField
																			error={
																				formikBag.errors.compensations?.[idx]
																					?.amount
																			}
																			showErrorMessage={false}
																			showErrorBorder>
																			<CompensationAmountInput
																				value={compensation.amount}
																				onChange={amount => {
																					formikBag.setFieldValue(
																						`compensations.${idx}.amount`,
																						amount
																					);
																				}}
																			/>
																		</FormField>
																	) : (
																		formatNumberWithCommas(compensation.amount)
																	)}
																</td>
																<td style={{ width: '35px' }}>
																	{isEditing ? (
																		<Button
																			variant="no-style"
																			onClick={async e => {
																				e.preventDefault();

																				try {
																					await buildValidationSchema().validateAt(
																						`compensations.${idx}`,
																						formikBag.values
																					);

																					formikBag.setFieldValue(
																						'editingCompensationIndex',
																						null
																					);
																				} catch {
																					// Do nothing
																				} finally {
																					formikBag.validateField(
																						`compensations.${idx}.type`
																					);
																					formikBag.validateField(
																						`compensations.${idx}.currency`
																					);
																					formikBag.validateField(
																						`compensations.${idx}.scheme`
																					);
																					formikBag.validateField(
																						`compensations.${idx}.amount`
																					);
																				}
																			}}>
																			<img
																				src="/ats/icons/check-icon.svg"
																				alt="Save"
																			/>
																		</Button>
																	) : (
																		<Button
																			variant="no-style"
																			onClick={e => {
																				e.preventDefault();
																				formikBag.setFieldValue(
																					'editingCompensationIndex',
																					idx
																				);
																			}}>
																			<img
																				src="/ats/icons/pencil-blue-icon.svg"
																				alt="Edit"
																			/>
																		</Button>
																	)}
																</td>
																<td style={{ width: '35px' }}>
																	<Button
																		variant="no-style"
																		onClick={e => {
																			e.preventDefault();
																			remove(idx);
																			isEditing &&
																				formikBag.setFieldValue(
																					'editingCompensationIndex',
																					null
																				);
																		}}
																		type="button">
																		<img
																			src="/ats/icons/trash-icon.svg"
																			alt="Delete"
																		/>
																	</Button>
																</td>
															</tr>
															{formikBag.values.editingCompensationIndex ===
																idx &&
																formikBag.errors.editingCompensationIndex && (
																	<tr className="form-field-error">
																		<td colSpan={5}>
																			{
																				formikBag.errors
																					.editingCompensationIndex
																			}
																		</td>
																	</tr>
																)}
														</>
													);
												}
											)
										}
									</FieldArray>
								</tbody>
							</table>
						</FormSection>
					</FormColumn>
				</FormRow>
			</Fragment>
		);
	}

	async urlToFile(url, filename) {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const mimeType = blob.type || 'image/jpeg';
			const extension = mimeType.split('/')[1];
			return new File([blob], `${filename}.${extension}`, { type: mimeType });
		} catch (error) {
			console.error('Error converting URL to File:', error);
			return null;
		}
	}

	async create(values) {
		const {
			name,
			email,
			phoneNumber,
			seniority,
			city,
			profileImage,
			linkedinUrl,
			githubUrl,
			portfolioUrl,
			englishLevel,
			ownerId,
			sourceId,
			referrerId,
			countryId,
			hireability,
			potentialJobRoles,
			tags,
			additionalTags,
			compensations
		} = values;

		// Convert profileImage URL to File if it's a string (imported data)
		let processedProfileImage = profileImage;
		if (profileImage && typeof profileImage === 'string') {
			processedProfileImage = await this.urlToFile(
				profileImage,
				`profile-${name.replace(/\s+/g, '-').toLowerCase()}`
			);
		}

		const normalizedEmail =
			typeof email === 'string' ? email.trim() || null : (email ?? null);

		applicantStore
			.create({
				name,
				email: normalizedEmail,
				phoneNumber,
				seniority,
				city,
				profileImage: processedProfileImage,
				linkedinUrl: linkedinUrl || null,
				githubUrl,
				portfolioUrl,
				englishLevel,
				ownerId,
				sourceId,
				referrerId,
				countryId,
				hireability,
				potentialJobRoles,
				tags: [...tags, ...additionalTags],
				compensations: compensations
					.map(compensation => ({
						...compensation,
						type: compensation.type.value,
						scheme: compensation.scheme.value,
						currencyId: compensation.currency.id
					}))
					.reverse() // Reverse the array to create in the order the compensations were added
			})
			.then(result => {
				if (result.status === 'success') {
					notificationStore.pushNotification({
						type: notificationStore.SUCCESS_NOTIFICATION,
						title: 'Create Candidate',
						message: 'The candidate has been successfully created.'
					});
					this.closeWithSuccess(result);
					openApplicantProfile(result.data?.id, {
						onSuccess: () => this.props.handleSuccess?.()
					});
				} else {
					const existingApplicantId = result.data?.applicantId;
					if (existingApplicantId) {
						notifyDuplicateApplicant({
							applicantId: existingApplicantId,
							message: result.message
						});
					} else {
						notificationStore.pushNotification({
							type: notificationStore.ERROR_NOTIFICATION,
							title: 'Create Candidate',
							message:
								result.message || 'An error has occurred. Please try again.'
						});
					}
					this.saving(false);
				}
			});
	}
}
